/**
 * Heap Analytics Helper Functions
 *
 * Provides typed wrappers for Heap Analytics SDK.
 * Used after initializeHeap() has been called.
 *
 * @see https://developers.heap.io/reference/client-side-apis-overview
 */

/**
 * Identify a user in Heap Analytics
 *
 * Call this after authentication to associate events with a user.
 * Safe to call before Heap is fully loaded - events will be queued.
 *
 * @param userId - Unique user identifier
 * @param properties - Optional user properties (email, name, etc.)
 *
 * @example
 * identifyHeapUser(user.id, { email: user.email, firstName: user.firstName });
 */
export function identifyHeapUser(
  userId: string,
  properties?: Record<string, unknown>
): void {
  if (!globalThis.heap) {
    return;
  }

  globalThis.heap.identify(userId);

  if (properties) {
    globalThis.heap.addUserProperties(properties);
  }
}

/**
 * Track a custom event in Heap Analytics
 *
 * @param eventName - Name of the event (e.g., 'Button Clicked', 'Form Submitted')
 * @param properties - Optional event properties
 *
 * @example
 * trackHeapEvent('Reservation Created', { reservationId: '123', type: 'training' });
 */
export function trackHeapEvent(
  eventName: string,
  properties?: Record<string, unknown>
): void {
  globalThis.heap?.track(eventName, properties);
}

/**
 * Reset user identity in Heap Analytics
 *
 * Call this on logout or when cookies are rejected to clear user association.
 * Creates a new anonymous session.
 */
export function resetHeapIdentity(): void {
  globalThis.heap?.resetIdentity();
}
